/**
 * Accommodation Finder — Google Maps + AI-powered lodging search
 *
 * Layout: two-panel (preferences + results left, interactive Google Map right)
 * Flow: User fills preferences → Groq interprets → Google Places search → map + cards
 */
/* global google */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  APIProvider,
  Map as GoogleMap,
  Marker,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StarIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  accommodationsAPI,
  type AccommodationFinderRequest,
  type AccommodationFinderResponse,
  type AccommodationResult,
} from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const ACCOMMODATION_TYPES = [
  'Hotel', 'B&B / Guesthouse', 'Hostel', 'Apartment / Rental', 'Boutique Hotel', 'Resort',
];

const BUDGET_LEVELS = [
  { value: 'budget', label: 'Budget' },
  { value: 'mid-range', label: 'Mid-range' },
  { value: 'luxury', label: 'Luxury' },
];

const GROUP_TYPES = [
  { value: '', label: 'Any' },
  { value: 'solo', label: 'Solo' },
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family' },
  { value: 'group', label: 'Group' },
];

const ARRIVAL_MODES = [
  { value: '', label: 'N/A' },
  { value: 'car', label: 'Car' },
  { value: 'plane', label: 'Plane' },
  { value: 'train', label: 'Train' },
  { value: 'bus', label: 'Bus' },
];

const ROOM_PREFS = [
  'Queen/King bed', 'Twin beds', 'Private bathroom', 'En-suite shower',
  'WiFi', 'Parking', 'Kitchen/Kitchenette', 'Breakfast included',
  'Air conditioning', 'Balcony/Terrace', 'Sea view', 'Pool',
  'Pet-friendly', 'Wheelchair accessible', 'Laundry',
];

const PRICE_LABELS: Record<number, string> = {
  0: 'Free', 1: '$', 2: '$$', 3: '$$$', 4: '$$$$',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Property card shown in the results panel */
const PropertyCard: React.FC<{
  result: AccommodationResult;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}> = ({ result, isSelected, onClick, onMouseEnter }) => (
  <div
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className={`flex gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
      isSelected
        ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 dark:border-teal-600 shadow-md'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-teal-300 hover:shadow-sm'
    }`}
  >
    {/* Photo */}
    {result.photo_url ? (
      <img
        src={result.photo_url}
        alt={result.name}
        className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
        loading="lazy"
      />
    ) : (
      <div className="w-20 h-20 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
        <BuildingOfficeIcon className="w-8 h-8 text-gray-300 dark:text-gray-500" />
      </div>
    )}

    {/* Info */}
    <div className="flex-1 min-w-0">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{result.name}</h3>

      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
        {result.rating && (
          <span className="flex items-center gap-0.5 text-xs font-medium">
            <StarIconSolid className="w-3.5 h-3.5 text-amber-400" />
            {result.rating.toFixed(1)}
          </span>
        )}
        {result.review_count && (
          <span className="text-xs text-gray-400">({result.review_count})</span>
        )}
        {result.property_type && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            {result.property_type}
          </span>
        )}
        {result.price_level != null && result.price_level > 0 && (
          <span className="text-xs font-medium text-teal-600 dark:text-teal-400">
            {PRICE_LABELS[result.price_level] || ''}
          </span>
        )}
      </div>

      {result.why_good && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{result.why_good}</p>
      )}

      {result.tripadvisor_rating && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-xs font-medium text-green-700 dark:text-green-400">TA {result.tripadvisor_rating}</span>
          {result.tripadvisor_summary && (
            <span className="text-xs text-gray-400 truncate">{result.tripadvisor_summary}</span>
          )}
        </div>
      )}

      {result.match_score && (
        <div className="flex items-center gap-1 mt-1">
          <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                result.match_score >= 80 ? 'bg-teal-500' : result.match_score >= 60 ? 'bg-amber-400' : 'bg-gray-400'
              }`}
              style={{ width: `${result.match_score}%` }}
            />
          </div>
          <span className="text-xs text-gray-400">{result.match_score}%</span>
        </div>
      )}

      {/* Links */}
      <div className="flex items-center gap-2 mt-1.5">
        <a href={result.google_maps_url} target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-xs text-teal-600 dark:text-teal-400 hover:underline">Google Maps</a>
        <a href={result.tripadvisor_url} target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-xs text-green-600 dark:text-green-400 hover:underline">TripAdvisor</a>
      </div>
    </div>
  </div>
);

/** Map auto-pan component — fits bounds to all results, pans to selected */
const MapController: React.FC<{
  results: AccommodationResult[];
  selectedPlace: AccommodationResult | null;
}> = ({ results, selectedPlace }) => {
  const map = useMap();

  // Fit bounds to all results when results change
  useEffect(() => {
    if (!map || results.length === 0) return;
    const pts = results.filter(r => r.lat && r.lon);
    if (pts.length === 0) return;

    if (pts.length === 1) {
      map.setCenter({ lat: pts[0].lat!, lng: pts[0].lon! });
      map.setZoom(15);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    pts.forEach(r => bounds.extend({ lat: r.lat!, lng: r.lon! }));
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }, [map, results]);

  // Pan to selected place
  useEffect(() => {
    if (!map || !selectedPlace?.lat || !selectedPlace?.lon) return;
    map.panTo({ lat: selectedPlace.lat, lng: selectedPlace.lon });
    if (map.getZoom()! < 14) map.setZoom(15);
  }, [map, selectedPlace]);

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export const AccommodationGuide: React.FC = () => {
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');

  // Form state
  const [destination, setDestination] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [nights, setNights] = useState<number | ''>('');
  const [accomTypes, setAccomTypes] = useState<string[]>([]);
  const [budget, setBudget] = useState('mid-range');
  const [roomPrefs, setRoomPrefs] = useState<string[]>([]);
  const [arrivalMode, setArrivalMode] = useState('');
  const [groupType, setGroupType] = useState('');
  const [otherPrefs, setOtherPrefs] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formExpanded, setFormExpanded] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [infoWindowId, setInfoWindowId] = useState<string | null>(null);

  // Map bounds filtering
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);

  // Results
  const [response, setResponse] = useState<AccommodationFinderResponse | null>(null);

  const resultsRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<globalThis.Map<string, HTMLDivElement>>(new globalThis.Map());

  // Filter results to only those visible in the current map viewport
  const visibleResults = useMemo(() => {
    if (!response?.results) return [];
    if (!mapBounds) return response.results;
    return response.results.filter(r => {
      if (!r.lat || !r.lon) return false;
      return r.lat >= mapBounds.south && r.lat <= mapBounds.north
          && r.lon >= mapBounds.west && r.lon <= mapBounds.east;
    });
  }, [response?.results, mapBounds]);

  const toggleAccomType = (t: string) => {
    setAccomTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const toggleRoomPref = (pref: string) => {
    setRoomPrefs(prev => prev.includes(pref) ? prev.filter(p => p !== pref) : [...prev, pref]);
  };

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!destination.trim()) return;

    setLoading(true);
    setError(null);
    setSelectedId(null);
    setInfoWindowId(null);

    const req: AccommodationFinderRequest = {
      destination: destination.trim(),
      budget_level: budget,
    };
    if (neighborhood.trim()) req.neighborhood = neighborhood.trim();
    if (checkIn) req.check_in = checkIn;
    if (checkOut) req.check_out = checkOut;
    if (nights) req.nights = Number(nights);
    if (accomTypes.length > 0) req.accommodation_types = accomTypes;
    if (roomPrefs.length > 0) req.room_preferences = roomPrefs;
    if (arrivalMode) req.arrival_mode = arrivalMode;
    if (groupType) req.group_type = groupType;
    if (otherPrefs.trim()) req.other_preferences = otherPrefs.trim();

    try {
      const data = await accommodationsAPI.find(req);
      setResponse(data);
      setFormExpanded(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [destination, neighborhood, checkIn, checkOut, nights, accomTypes, budget, roomPrefs, arrivalMode, groupType, otherPrefs]);

  const handleCardClick = (result: AccommodationResult) => {
    setSelectedId(result.place_id);
    setInfoWindowId(result.place_id);
  };

  const scrollToCard = (placeId: string) => {
    const el = cardRefs.current.get(placeId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleNewSearch = useCallback(() => {
    setResponse(null);
    setSelectedId(null);
    setHoveredId(null);
    setInfoWindowId(null);
    setError(null);
    setDestination('');
    setNeighborhood('');
    setCheckIn('');
    setCheckOut('');
    setNights('');
    setAccomTypes([]);
    setBudget('mid-range');
    setRoomPrefs([]);
    setArrivalMode('');
    setGroupType('');
    setOtherPrefs('');
    setFormExpanded(true);
  }, []);

  const activeId = hoveredId || selectedId;

  const inputCls = "w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:[color-scheme:dark]";
  const labelCls = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

  // No API key warning
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-300">Google Maps API Key Required</h2>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">
                The Accommodation Finder requires a Google Maps API key with <strong>Maps JavaScript API</strong> and <strong>Places API</strong> enabled.
              </p>
              <ol className="text-sm text-amber-700 dark:text-amber-400 mt-3 space-y-1 list-decimal list-inside">
                <li>Go to Google Cloud Console and create a project</li>
                <li>Enable Maps JavaScript API and Places API</li>
                <li>Create an API key with those APIs</li>
                <li>Add <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">GOOGLE_MAPS_API_KEY=your_key</code> to your <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">.env</code> file</li>
                <li>Rebuild and restart Docker containers</li>
              </ol>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-3">
                Free tier: $200/month credit (~28K map loads + ~11K places searches)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className={`flex overflow-hidden ${isMobile ? 'flex-col h-[calc(100vh-64px-56px)]' : 'flex-row h-[calc(100vh-64px)]'}`}>
        <LoadingOverlay isVisible={loading} message="Finding accommodations..." subMessage="Searching properties and analyzing matches" />

        {/* ── Left Panel: Form + Results ──────────────────────────────── */}
        <div className={`flex flex-col bg-gray-50 dark:bg-gray-900 overflow-y-auto ${
          isMobile
            ? (mobileView === 'list' || !response ? 'flex-1' : 'hidden')
            : 'w-[420px] flex-shrink-0 border-r border-gray-200 dark:border-gray-700'
        }`}>

          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BuildingOfficeIcon className="w-5 h-5 text-teal-600" />
              Accommodation Finder
            </h1>
            {response && (
              <div className="flex items-center justify-between mt-0.5">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {visibleResults.length === response.total_found
                    ? `${response.total_found} ${response.total_found === 1 ? 'property' : 'properties'} found`
                    : `${visibleResults.length} of ${response.total_found} in view`}
                  {response.area_summary && ` — ${response.area_summary}`}
                </p>
                <button onClick={handleNewSearch}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30 rounded-lg transition-colors">
                  <MagnifyingGlassIcon className="w-3 h-3" />
                  New Search
                </button>
              </div>
            )}
          </div>

          {/* Search Form (collapsible) */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <button
              onClick={() => setFormExpanded(!formExpanded)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <span className="flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                Search Preferences
              </span>
              {formExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
            </button>

            {formExpanded && (
              <form onSubmit={handleSearch} className="px-4 pb-4 space-y-3">
                {/* Row 1: Destination + Neighborhood */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Destination *</label>
                    <input type="text" value={destination} onChange={e => setDestination(e.target.value)}
                      placeholder="e.g., Dubrovnik" className={inputCls} required />
                  </div>
                  <div>
                    <label className={labelCls}>Neighborhood</label>
                    <input type="text" value={neighborhood} onChange={e => setNeighborhood(e.target.value)}
                      placeholder="e.g., Old Town" className={inputCls} />
                  </div>
                </div>

                {/* Row 2: Dates + Nights */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelCls}>Check-in</label>
                    <div className="relative">
                      <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
                        className={`${inputCls} date-input-dark`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Check-out</label>
                    <div className="relative">
                      <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)}
                        className={`${inputCls} date-input-dark`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Nights</label>
                    <input type="number" min={1} max={30} value={nights} onChange={e => setNights(e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="2" className={inputCls} />
                  </div>
                </div>

                {/* Row 3: Lodging types (multi-select) */}
                <div>
                  <label className={labelCls}>Lodging Type(s)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ACCOMMODATION_TYPES.map(t => (
                      <button key={t} type="button" onClick={() => toggleAccomType(t)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          accomTypes.includes(t)
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
                        }`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 4: Budget + Group */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Budget</label>
                    <select value={budget} onChange={e => setBudget(e.target.value)} className={inputCls}>
                      {BUDGET_LEVELS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Group</label>
                    <select value={groupType} onChange={e => setGroupType(e.target.value)} className={inputCls}>
                      {GROUP_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Row 4: Arrival mode */}
                <div>
                  <label className={labelCls}>Arriving by</label>
                  <div className="flex gap-1.5">
                    {ARRIVAL_MODES.map(m => (
                      <button key={m.value} type="button" onClick={() => setArrivalMode(m.value)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          arrivalMode === m.value
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
                        }`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 5: Room preferences */}
                <div>
                  <label className={labelCls}>Room Preferences</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ROOM_PREFS.map(pref => (
                      <button key={pref} type="button" onClick={() => toggleRoomPref(pref)}
                        className={`px-2 py-1 rounded-full text-xs transition-colors ${
                          roomPrefs.includes(pref)
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}>
                        {pref}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 6: Other preferences */}
                <div>
                  <label className={labelCls}>Other Preferences</label>
                  <textarea value={otherPrefs} onChange={e => setOtherPrefs(e.target.value)}
                    placeholder="e.g., Close to restaurants, quiet area, good for remote work..."
                    rows={2}
                    className={`${inputCls} resize-none`} />
                </div>

                {/* Submit */}
                <button type="submit" disabled={loading || !destination.trim()}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors">
                  {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <MagnifyingGlassIcon className="w-4 h-4" />}
                  {loading ? 'Searching...' : 'Find Accommodations'}
                </button>
              </form>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mx-4 mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-red-700 dark:text-red-300">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><XMarkIcon className="w-3.5 h-3.5" /></button>
            </div>
          )}

          {/* Results list */}
          <div ref={resultsRef} className="flex-1 overflow-y-auto p-3 space-y-2">
            {!response && !loading && (
              <div className="text-center py-16 px-4">
                <BuildingOfficeIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Fill in your preferences and search to find accommodations on the map
                </p>
              </div>
            )}

            {response && response.results.length === 0 && (
              <div className="text-center py-12 px-4">
                <MapPinIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No properties found. Try broadening your search.</p>
              </div>
            )}

            {response && response.results.length > 0 && visibleResults.length === 0 && (
              <div className="text-center py-8 px-4">
                <MapPinIcon className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No properties in this area. Zoom out to see more.</p>
              </div>
            )}

            {visibleResults.map(result => (
              <div
                key={result.place_id}
                ref={el => { if (el) cardRefs.current.set(result.place_id, el); }}
              >
                <PropertyCard
                  result={result}
                  isSelected={activeId === result.place_id}
                  onClick={() => handleCardClick(result)}
                  onMouseEnter={() => setHoveredId(result.place_id)}
                />
              </div>
            ))}

            {/* Area tips panel */}
            {response && (response.parking_notes || response.area_tips || response.booking_tip) && (
              <div className="mt-3 space-y-2">
                {response.parking_notes && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Parking</h4>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{response.parking_notes}</p>
                  </div>
                )}
                {response.area_tips && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Area Tips</h4>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{response.area_tips}</p>
                  </div>
                )}
                {response.booking_tip && (
                  <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800 p-3">
                    <h4 className="text-xs font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-1">Booking Tip</h4>
                    <p className="text-xs text-teal-700 dark:text-teal-300">{response.booking_tip}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Right Panel: Google Map ─────────────────────────────────── */}
        <div className={`relative ${
          isMobile
            ? (mobileView === 'map' && response ? 'flex-1' : 'hidden')
            : 'flex-1'
        }`} onMouseLeave={() => setHoveredId(null)}>
          <GoogleMap
            defaultCenter={{ lat: 42.65, lng: 18.09 }}
            defaultZoom={13}
            gestureHandling="greedy"
            disableDefaultUI={false}
            zoomControl={true}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={true}
            className="w-full h-full"
            onIdle={(ev) => {
              const m = ev.map;
              const b = m.getBounds();
              if (b) {
                const ne = b.getNorthEast();
                const sw = b.getSouthWest();
                setMapBounds({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() });
              }
            }}
          >
            <MapController results={response?.results || []} selectedPlace={response?.results.find(r => r.place_id === selectedId) || null} />

            {response?.results.map(result => {
              if (!result.lat || !result.lon) return null;
              const isActive = activeId === result.place_id;
              return (
                <Marker
                  key={result.place_id}
                  position={{ lat: result.lat, lng: result.lon }}
                  onClick={() => {
                    setSelectedId(result.place_id);
                    setInfoWindowId(result.place_id);
                    scrollToCard(result.place_id);
                  }}
                  zIndex={isActive ? 100 : (result.match_score || 50)}
                  label={{
                    text: result.rating ? result.rating.toFixed(1) : '?',
                    color: isActive ? '#ffffff' : '#1f2937',
                    fontSize: '11px',
                    fontWeight: 'bold',
                  }}
                  icon={{
                    path: 'M-12,-12 L12,-12 L12,12 L-12,12 Z',
                    fillColor: isActive ? '#0d9488' : '#ffffff',
                    fillOpacity: 1,
                    strokeColor: isActive ? '#0d9488' : (result.match_score && result.match_score >= 80 ? '#14b8a6' : '#d1d5db'),
                    strokeWeight: isActive ? 2 : 1.5,
                    scale: 1,
                    labelOrigin: new google.maps.Point(0, 0),
                  }}
                />
              );
            })}

            {/* Info Window for selected marker */}
            {infoWindowId && (() => {
              const place = response?.results.find(r => r.place_id === infoWindowId);
              if (!place?.lat || !place?.lon) return null;
              return (
                <InfoWindow
                  position={{ lat: place.lat, lng: place.lon }}
                  onCloseClick={() => setInfoWindowId(null)}
                  pixelOffset={[0, -30]}
                >
                  <div style={{ maxWidth: 260, padding: '4px 0' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {place.photo_url && (
                        <img src={place.photo_url} alt={place.name}
                          style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover' }} />
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{place.name}</div>
                        {place.rating && (
                          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                            {'★'.repeat(Math.round(place.rating))} {place.rating.toFixed(1)}
                            {place.review_count ? ` (${place.review_count})` : ''}
                          </div>
                        )}
                        {place.property_type && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{place.property_type}</div>
                        )}
                      </div>
                    </div>
                    {place.why_good && (
                      <div style={{ fontSize: 11, color: '#555', marginTop: 6, lineHeight: 1.4 }}>
                        {place.why_good}
                      </div>
                    )}
                    {place.concerns && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, lineHeight: 1.4 }}>
                        Note: {place.concerns}
                      </div>
                    )}
                    {place.tripadvisor_rating && (
                      <div style={{ fontSize: 11, color: '#15803d', marginTop: 4 }}>
                        TripAdvisor: {place.tripadvisor_rating}
                        {place.tripadvisor_summary && ` — ${place.tripadvisor_summary}`}
                      </div>
                    )}
                    <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                      <a href={place.google_maps_url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: '#0e7490', textDecoration: 'none' }}>
                        Google Maps ↗
                      </a>
                      <a href={place.tripadvisor_url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: '#15803d', textDecoration: 'none' }}>
                        TripAdvisor ↗
                      </a>
                    </div>
                  </div>
                </InfoWindow>
              );
            })()}
          </GoogleMap>

          {/* Map overlay: AI badge */}
          {response && (
            <div className="absolute top-3 left-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md border border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <SparklesIcon className="w-3.5 h-3.5 text-teal-600" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                AI-ranked results for {response.destination}
              </span>
            </div>
          )}
        </div>

        {/* ── Mobile: List/Map toggle bar ─────────────────────────────── */}
        {isMobile && response && (
          <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 safe-area-bottom">
            <button
              onClick={() => setMobileView('list')}
              className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
                mobileView === 'list'
                  ? 'text-teal-600 dark:text-teal-400 border-b-2 border-teal-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              List {response.results.length > 0 ? `(${visibleResults.length})` : ''}
            </button>
            <button
              onClick={() => setMobileView('map')}
              className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
                mobileView === 'map'
                  ? 'text-teal-600 dark:text-teal-400 border-b-2 border-teal-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Map
            </button>
          </div>
        )}
      </div>
    </APIProvider>
  );
};

export default AccommodationGuide;
